import React from 'react';
import { getImageSrc, handleImageError, formatDisplayText } from '../utils/showcaseUtils';

export default function ConstituentProductModal({ product, onClose }) {
  const [imageError, setImageError] = React.useState(false);

  const handleImageLoadError = (e) => {
    setImageError(true);
    handleImageError(e);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl max-h-[90vh] overflow-y-auto w-full border border-[#19BFB7]/20">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-[#011841]">{product.name || 'Product Details'}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Product Image - Large */}
          <div className="mb-6">
            <div className="relative h-96 bg-white rounded-lg overflow-hidden max-w-2xl mx-auto">
              {!imageError && product.imageUrl ? (
                <img
                  src={getImageSrc(product.imageUrl)}
                  alt={product.name || 'Product image'}
                  className="w-full h-full object-contain"
                  onError={handleImageLoadError}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <div className="text-6xl mb-2">🛍️</div>
                    <p className="text-sm">No image available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Product Details - Full Width Column */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-bold text-[#011841] mb-3">Product Information</h3>
              <div className="space-y-2">
                {product.code && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600">Product Code: </span>
                    <span className="font-medium text-[#011841]">{product.code}</span>
                  </div>
                )}
                {product.BrowsingGroup && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600">BrowsingGroup: </span>
                    <span className="font-medium text-[#011841]">{product.BrowsingGroup}</span>
                  </div>
                )}
              </div>
            </div>

            {/* USP/Features */}
            {product.USP && (
              <div>
                <h3 className="text-lg font-bold text-[#011841] mb-3">✨ Key Features</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-[#033EA5] whitespace-pre-line">{product.USP}</p>
                </div>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div>
                <h3 className="text-lg font-bold text-[#011841] mb-3">📝 Description</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
                </div>
              </div>
            )}

            {/* Categories */}
            {product.Categories && (
              <div>
                <h3 className="text-lg font-bold text-[#011841] mb-3">🏷️ Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {product.Categories.split(',').map((cat, index) => {
                    const cleanCat = cat.trim();
                    const match = cleanCat.match(/^[^ ]+ (.+)$/);
                    const displayName = match ? match[1].trim() : cleanCat;
                    return (
                      <span
                        key={index}
                        className="bg-[#19BFB7]/10 text-[#033EA5] px-3 py-1 rounded-full text-sm"
                      >
                        {displayName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Showcase Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>🎯 Constituent Product:</strong> This is a component of a SET product. No pricing or ordering functionality is available.
              </p>
            </div>

            {/* Close Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={onClose}
                className="border-2 border-[#19BFB7]/30 rounded-lg hover:bg-[#E8F4F8] text-[#033EA5] px-8 py-3 font-medium transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
